import React from 'react';
import { Globe, BarChart2, Clock, ChevronRight, Plus } from 'lucide-react';
import { PageHeader, PageShell, SectionCard, Badge } from '../components/PeecUI';

interface ProjectsPageProps {
  workspaceId: string;
}

const projects = [
  { id: 'workspace_tradingbase', name: 'tradingbase', domain: 'tradingbase.ai', plan: 'professional', prompts: 50, domains: 289, lastCrawl: '2小时前', status: 'active' },
  { id: 'workspace_cryptoedge', name: 'CryptoEdge', domain: 'cryptoedge.com', plan: 'professional', prompts: 38, domains: 178, lastCrawl: '3小时前', status: 'active' },
  { id: 'workspace_finpulse', name: 'FinPulse', domain: 'finpulse.io', plan: 'starter', prompts: 72, domains: 412, lastCrawl: '6小时前', status: 'active' },
  { id: 'workspace_marketlens', name: 'MarketLens', domain: 'marketlens.co', plan: 'enterprise', prompts: 95, domains: 563, lastCrawl: '1小时前', status: 'active' },
  { id: 'workspace_huaxing', name: '华星精工', domain: 'huaxing-precision.cn', plan: 'professional', prompts: 32, domains: 96, lastCrawl: '5小时前', status: 'active' },
];

const planBadge = (plan: string) => {
  if (plan === 'enterprise') return <Badge tone="purple">企业版</Badge>;
  if (plan === 'professional') return <Badge tone="blue">专业版</Badge>;
  return <Badge tone="slate">入门版</Badge>;
};

const ProjectsPage: React.FC<ProjectsPageProps> = () => {
  return (
    <PageShell>
      <PageHeader title="项目管理" description="管理您的所有工作空间与 GEO 监控项目" />

      <div className="mb-5 flex items-center justify-between">
        <div className="text-sm text-slate-500">{projects.length} 个活跃项目</div>
        <button className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors">
          <Plus size={15} /> 新建项目
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {projects.map((project) => (
          <div key={project.id} className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-violet-200 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <img
                  src={`https://www.google.com/s2/favicons?domain=${project.domain}&sz=64`}
                  alt=""
                  className="w-9 h-9 rounded-lg border border-slate-100"
                />
                <div>
                  <div className="font-semibold text-slate-950">{project.name}</div>
                  <div className="text-xs text-slate-400">{project.domain}</div>
                </div>
              </div>
              {planBadge(project.plan)}
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-slate-50 p-2.5 text-center">
                <div className="text-lg font-bold text-slate-950">{project.prompts}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">提示词</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-2.5 text-center">
                <div className="text-lg font-bold text-slate-950">{project.domains}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">域名</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-2.5 text-center">
                <div className="text-xs font-semibold text-emerald-600">活跃</div>
                <div className="text-[10px] text-slate-400 mt-0.5">状态</div>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock size={11} /> 最近采集 {project.lastCrawl}</span>
              <span className="flex items-center gap-0.5 text-violet-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                进入项目 <ChevronRight size={12} />
              </span>
            </div>
          </div>
        ))}

        {/* Add project card */}
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-8 text-center hover:border-violet-300 hover:bg-violet-50/30 transition-all cursor-pointer group">
          <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-3 group-hover:border-violet-300 transition-colors">
            <Plus size={18} className="text-slate-400 group-hover:text-violet-500 transition-colors" />
          </div>
          <div className="text-sm font-medium text-slate-600 group-hover:text-violet-700">新建项目</div>
          <div className="text-xs text-slate-400 mt-1">添加新的品牌或域名进行监控</div>
        </div>
      </div>

      <SectionCard className="mt-6" title="项目配额使用" subtitle="当前订阅计划下的资源使用情况">
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          {[
            { label: '活跃项目', used: 5, total: 10, icon: <Globe size={15} className="text-violet-500" /> },
            { label: '提示词监控总量', used: 287, total: 500, icon: <BarChart2 size={15} className="text-blue-500" /> },
            { label: '每日 API 调用', used: 7240, total: 10000, icon: <Clock size={15} className="text-amber-500" /> },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-slate-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                {item.icon}
                <span className="text-xs font-medium text-slate-600">{item.label}</span>
              </div>
              <div className="flex items-end justify-between mb-1.5">
                <span className="text-xl font-bold text-slate-950">{item.used.toLocaleString()}</span>
                <span className="text-xs text-slate-400">/ {item.total.toLocaleString()}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className="bg-violet-500 h-1.5 rounded-full"
                  style={{ width: `${Math.min(100, (item.used / item.total) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default ProjectsPage;
